/**
 * TrustStrip (E5) - Mid-page trust strip with 3 anchor cards.
 *
 * Renders Accuracy + Freshness + Compliance signals between stats-band and
 * the "Three modes" features intro on the landing page. Copy lives in
 * `../i18n/landing-trust-strip.ts` (legal-upl reviewed - do not modify here).
 *
 * Layout:
 *   - Desktop: 3 cards flex row, equal width.
 *   - Tablet/mobile (<=900px): horizontal scroll with snap, min-width 280px.
 *   - Mobile (<=600px): same scroll pattern, min-width 260px.
 *
 * Hover uses `--asklaw-navy` border tint to distinguish from feature tiles
 * (which hover orange). prefers-reduced-motion is honored globally via the
 * Reveal wrapper + the @media block in global.css.
 */
import { Link } from 'react-router-dom';
import { trustStripVi, trustStripEn } from '../i18n/landing-trust-strip';
import type { TrustStripCatalog } from '../i18n/landing-trust-strip';

type Lang = 'vi' | 'en';

export function TrustStrip({ lang }: { lang: Lang }) {
  const c = lang === 'en' ? trustStripEn : trustStripVi;
  const stripEyebrow =
    lang === 'en' ? 'TRANSPARENCY + COMPLIANCE' : 'MINH BẠCH + TUÂN THỦ';

  return (
    <div className="trust-strip">
      <p className="trust-strip-eyebrow">{stripEyebrow}</p>
      <div className="trust-strip-grid">
        <TrustCard
          eyebrow={c.card_a_eyebrow}
          heading={c.card_a_heading}
          body={c.card_a_body}
          linkLabel={c.card_a_link_label}
          linkHref={c.card_a_link_href}
        />
        <TrustCard
          eyebrow={c.card_b_eyebrow}
          heading={c.card_b_heading}
          body={c.card_b_body}
          linkLabel={c.card_b_link_label}
          linkHref={c.card_b_link_href}
        />
        <TrustCard
          eyebrow={c.card_c_eyebrow}
          heading={c.card_c_heading}
          body={c.card_c_body}
          linkLabel={c.card_c_link_label}
          linkHref={c.card_c_link_href}
        />
      </div>
    </div>
  );
}

function TrustCard({
  eyebrow,
  heading,
  body,
  linkLabel,
  linkHref,
}: {
  eyebrow: TrustStripCatalog['card_a_eyebrow'];
  heading: TrustStripCatalog['card_a_heading'];
  body: TrustStripCatalog['card_a_body'];
  linkLabel: TrustStripCatalog['card_a_link_label'];
  linkHref: TrustStripCatalog['card_a_link_href'];
}) {
  return (
    <Link to={linkHref} className="trust-card">
      <span className="trust-card-eyebrow">{eyebrow}</span>
      <h3 className="trust-card-heading">{heading}</h3>
      <p className="trust-card-body">{body}</p>
      <span className="trust-card-link">
        {linkLabel}
        <svg className="arrow" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
          <path d="M2.5 8h9.5M8.5 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </Link>
  );
}
